/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Reducers of wizard
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.2.0
 */

import produce from 'immer';

import { LOGOUT_START_SUCCESSFUL } from '../../pages/PageAuth/constants/authConstants';
import { EBasketType } from '../app/appConstants';
import { EWizardReportParam } from './components/WizardReport/WizardReportConstants';
import { IWizardReportParam } from './components/WizardReport/wizardReportTypes';
import { TWizardActions } from './wizardActions';
import {
    EWizardName,
    WIZARD_ALL_ENTITIES,
    WIZARD_LOADER_START,
    WIZARD_LOADER_STOP,
    WIZARD_META_SUCCESS,
    WIZARD_REPORT_SET_ERRORS,
    WIZARD_REPORT_SET_PARAM,
    WIZARD_REPORT_SET_PARAMS,
    WIZARD_RESTORE_ACTION,
    WIZARD_RESTORE_FAILED,
    WIZARD_RESTORE_SUCCESS,
    WIZARD_SET_ENTITY_ID,
    WIZARD_SET_IS_REQUESTS_STICKY,
    WIZARD_START_ACTION,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
    WIZARD_STORAGE_CLEAR_STEPS,
} from './wizardConstants';
import { TWizardStepNames } from './wizardTypes';

export interface IWizardState {
    basketSnapshotId: string;
    basketSnapshotType: string;
    basketType: EBasketType;
    entityId: number;
    error: string;
    isFinal: boolean;
    isShowLoader: boolean;
    isStart: boolean;
    isRequestsSticky: boolean;
    name: EWizardName;
    [EWizardReportParam.EMAIL]: IWizardReportParam;
    [EWizardReportParam.PHONE]: IWizardReportParam;
    [EWizardReportParam.EMAIL_DEST]: IWizardReportParam;
    [EWizardReportParam.SUBSCRIBERS_PHONE]: IWizardReportParam;
    [EWizardReportParam.SUBS_NOTIFICATION_NEEDED]: IWizardReportParam;
    [EWizardReportParam.ZIP]: IWizardReportParam;
    [EWizardReportParam.ZIP_PASSWORD]: IWizardReportParam;
    stepName: TWizardStepNames;
    steps: string[];
}

export const initialWizardNotificationParam: IWizardReportParam = {
    isEnabled: false,
    isValid: true,
    value: '',
};

export const initialWizardState: IWizardState = {
    basketSnapshotId: '',
    basketSnapshotType: '',
    basketType: EBasketType.ACCOUNT,
    entityId: WIZARD_ALL_ENTITIES,
    error: '',
    isFinal: false,
    isShowLoader: false,
    isStart: true,
    isRequestsSticky: false,
    name: EWizardName.MONEY_TRANSFER,
    [EWizardReportParam.EMAIL]: {
        ...initialWizardNotificationParam,
    },
    [EWizardReportParam.PHONE]: {
        ...initialWizardNotificationParam,
    },
    [EWizardReportParam.EMAIL_DEST]: {
        ...initialWizardNotificationParam,
    },
    [EWizardReportParam.SUBSCRIBERS_PHONE]: {
        ...initialWizardNotificationParam,
    },
    [EWizardReportParam.SUBS_NOTIFICATION_NEEDED]: {
        ...initialWizardNotificationParam,
    },
    [EWizardReportParam.ZIP]: {
        ...initialWizardNotificationParam,
    },
    [EWizardReportParam.ZIP_PASSWORD]: {
        ...initialWizardNotificationParam,
    },
    stepName: '',
    steps: [],
};

const wizardReducer = (state: IWizardState = initialWizardState, action: TWizardActions) =>
    produce(state, (draft) => {
        switch (action.type) {
            case WIZARD_START_ACTION:
                if (action.payload) {
                    draft.name = action.payload.name;
                    draft.basketType = action.payload.basketType;
                }
                break;
            case WIZARD_RESTORE_ACTION:
                draft.name = action.payload.name;
                draft.basketType = action.payload.basketType;
                draft.basketSnapshotId = action.payload.basketSnapshotId;
                draft.basketSnapshotType = action.payload.basketSnapshotType;
                break;
            case WIZARD_START_SUCCESS:
            case WIZARD_RESTORE_SUCCESS:
                draft.basketSnapshotId = action.payload.basketSnapshotId;
                draft.basketSnapshotType = action.payload.basketSnapshotType;
                break;
            case WIZARD_START_FAILED:
            case WIZARD_RESTORE_FAILED:
                draft.error = action.payload.message;
                break;
            case WIZARD_META_SUCCESS:
                draft.isFinal = action.payload.step.isFinal;
                draft.isStart = action.payload.step.isFirst;
                draft.stepName = action.payload.step.name;
                draft.steps = action.payload.wizard.steps;
                break;
            case WIZARD_LOADER_START:
                draft.isShowLoader = true;
                break;
            case WIZARD_LOADER_STOP:
                draft.isShowLoader = false;
                break;
            case WIZARD_REPORT_SET_PARAM:
                if (action.payload.isEnabled !== undefined)
                    draft[action.payload.paramType].isEnabled = action.payload.isEnabled;
                draft[action.payload.paramType].isValid = true;
                if (action.payload.isValid !== undefined)
                    draft[action.payload.paramType].isValid = action.payload.isValid;
                if (action.payload.value !== undefined) draft[action.payload.paramType].value = action.payload.value;
                break;
            case WIZARD_REPORT_SET_ERRORS:
                if (action.payload[EWizardReportParam.EMAIL]) draft[EWizardReportParam.EMAIL].isValid = false;
                if (action.payload[EWizardReportParam.PHONE]) draft[EWizardReportParam.PHONE].isValid = false;
                break;
            case WIZARD_REPORT_SET_PARAMS:
                draft[EWizardReportParam.EMAIL] = action.payload[EWizardReportParam.EMAIL];
                draft[EWizardReportParam.PHONE] = action.payload[EWizardReportParam.PHONE];
                draft[EWizardReportParam.EMAIL_DEST] = action.payload[EWizardReportParam.EMAIL_DEST];
                draft[EWizardReportParam.ZIP] = action.payload[EWizardReportParam.ZIP];
                draft[EWizardReportParam.ZIP_PASSWORD] = action.payload[EWizardReportParam.ZIP_PASSWORD];
                break;
            case WIZARD_SET_ENTITY_ID:
                draft.entityId = action.payload;
                break;
            case WIZARD_SET_IS_REQUESTS_STICKY:
                draft.isRequestsSticky = action.payload;
                break;
            case WIZARD_STORAGE_CLEAR_STEPS:
                draft.basketSnapshotId = '';
                draft.basketSnapshotType = '';
                break;

            case LOGOUT_START_SUCCESSFUL:
                return initialWizardState;
            default:
                break;
        }
    });

export default wizardReducer;
